/**
 * The Kundtjänst section: which CMS pages belong to it, and in what order.
 *
 * The section is a grouping that exists in three places on the storefront — the
 * utility bar's dropdown, the footer's first column, and the sidebar the pages
 * themselves render — and it used to be typed out separately in each. The two
 * copies had already drifted: the dropdown listed GDPR before Delbetalning and
 * the footer the other way round.
 *
 * Deliberately NOT a Magento category. A category's children are categories, so
 * making one would mean converting every page here into a PAGE-mode category and
 * changing its URL — for pages that are linked from the chrome of every page on
 * the site. The grouping is presentation, so it lives in presentation.
 *
 * The cost of that choice: adding a page to the section is an edit here rather
 * than something staff can do in Magento. That is the right trade for a list
 * that changes about once a year; if it starts changing more often, the same
 * pattern the footer uses for `opening_hours` — read it from a CMS block —
 * moves it back into the admin.
 *
 * `key` is a translation key under `footer`, which is where these labels already
 * lived when the footer was the only place that had them.
 */
export interface CustomerServiceLink {
  /** Store-relative path, without the store code — see StoreLink. */
  href: string;
  /** Translation key under the `footer` namespace. */
  key: string;
}

export const CUSTOMER_SERVICE_LINKS: readonly CustomerServiceLink[] = [
  { href: '/om-foretaget', key: 'aboutCompany' },
  { href: '/hitta-till-oss', key: 'findUs' },
  { href: '/leveransvillkor', key: 'deliveryTerms' },
  { href: '/sakerhet-och-sekretess', key: 'gdprSecurity' },
  { href: '/delbetalning', key: 'installments' },
  // The page exists in Magento (id 10) but is disabled, so this 404s until it
  // is switched on. Listed anyway: a visible 404 is the better failure, since
  // leaving the entry out would instead give a page that renders without its
  // section chrome once someone enables it, with nothing to point at the cause.
  { href: '/vanliga-fragor', key: 'faq' },
];

/**
 * Is this CMS page part of the section?
 *
 * Takes the url key as the catch-all route has it — no leading slash, no store
 * code — which is what `CmsPage` is handed.
 */
export function isCustomerServicePage(urlKey: string): boolean {
  const path = `/${urlKey.replace(/^\/+/, '')}`;
  return CUSTOMER_SERVICE_LINKS.some((link) => link.href === path);
}
